import React, { useState } from "react";
import Drawer from 'react-modern-drawer';
import 'react-modern-drawer/dist/index.css';
import { createPortal } from 'react-dom';

const IraCompletedHelpDrawer = () => {
  const [isOpen, setIsOpen] = useState(false);
  const toggle = () => setIsOpen(!isOpen);

  const drawer = (
    <div className="drawer-portal">
      <Drawer
        open={isOpen}
        onClose={toggle}
        direction="right"
        size={500}
        duration={400}
        overlayOpacity={0.5}
        overlayColor="#000"
        customIdSuffix="help-guide-ira-completed"
        className="help-guide-drawer"
        style={{ top: "73px", height: "calc(100vh - 73px)", transition: "all 0.4s ease-in-out" }}
      >
        <div className="drawer-content">
          <div className="drawer-header">
            <div className="header-title">
              <span className="emoji">🟢</span>
              <span className="title">IRA – Completed Tab Help</span>
            </div>
            <button className="close-button" onClick={toggle} aria-label="Close help panel">
              &times;
            </button>
          </div>

          <div className="drawer-body">
  <p>
    The Completed Accident IRA Detail Page shows details of an accident where the investigation 
    has been completed and the record is frozen. This page provides a full view of the incident, 
    investigation process, root cause findings, and final CAPA actions.
  </p>

  <hr />

  <h5 className="section-title">📑 Record Overview</h5>
  <ul>
    <li>Record Number – e.g., 940</li>
    <li>IRA Type – UA / UC (Unsafe Act / Unsafe Condition)</li>
    <li>Activity Name – e.g., Bar Bending</li>
    <li>Reported Month – e.g., July</li>
  </ul>

  <p>Below this, the timeline displays:</p>
  <ul>
    <li>Reported Details</li>
    <li>Incident Date</li>
    <li>Injured Person Details</li>
    <li>Discussion Comments (if any)</li>
  </ul>

  <hr />

  <h5 className="section-title">📂 Sections of the Page</h5>

  <p><b>1. Reported Details</b></p>
  <ul>
    <li>Evidence – images uploaded during accident reporting.</li>
    <li>Incident Description – summary of how the accident happened.</li>
  </ul>

  <p><b>2. Activity Information</b></p>
  <ul>
    <li>Service and linked Plant/Project/Client.</li>
    <li>Department/Contractor/Location for which the accident was reported.</li>
  </ul>

  <p><b>3. Incident Details</b></p>
  <ul>
    <li>Incident Date – when the accident occurred.</li>
    <li>Incident Time – exact time of the incident.</li>
    <li>Injured Person Details – who was injured.</li>
  </ul>

  <p><b>4. Investigation Start Details</b></p>
  <ul>
    <li>Shows when the investigation started (record moved from Pending → In-progress).</li>
  </ul>

  <p><b>5. Investigation Target & Team Details</b></p>
  <ul>
    <li>Investigation Target Date – by when investigation was expected to be completed.</li>
    <li>Team Member Names – with the crown icon for the team lead.</li>
  </ul>

  <hr />

  <h5 className="section-title">🔎 IRA Investigation Analysis – 5 Steps</h5>

  <p><b>Step 1 – Initial Response</b></p>
  <ul>
    <li>Displays the initial response actions taken after the accident was reported.</li>
  </ul>

  <p><b>Step 2 – Initial Investigation</b></p>
  <ul>
    <li>Injured person data: Identity, Employment Type, Gender, Age, Injury Type, Injured Body Part, Shift, Job Tenure, etc.</li>
    <li>5Y Analysis questions and answers linked to the injured person.</li>
  </ul>

  <p><b>Step 3 – Data Gathering</b></p>
  <ul>
    <li>Witness Data – witness names, type, photo, and statements.</li>
    <li>Documents – uploaded images or files under Paper/Digital, Physical Info, Position/Equipments, Sketch.</li>
    <li>Mandays Lost / Return to Work – numbers, costs, and supporting documents.</li>
    <li>5M Analysis – captured details for Management, Machinery, Manpower, Material, Method.</li>
    <li>Job Analysis Questions – responses and notes.</li>
  </ul>

  <p><b>Step 4 – Root Cause Analysis</b></p>
  <ul>
    <li>Method 1 – RCA (Contributing Factor, Immediate Factor, Root Causes via Fishbone Diagram)</li>
    <li>Method 2 – CLC (Comprehensive List of Causes): Causal Factor Type, Problem Category, Major/Near/Immediate/Final Root Cause</li>
    <li>Accident Costing – accident cost data if captured during investigation (only visible if enabled by admin).</li>
  </ul>

  <p><b>Step 5 – CAPA (Corrective & Preventive Actions)</b></p>
  <ul>
    <li>Final CAPA list approved by the Team Lead.</li>
    <li>Details of CAPA category, type (CA or PA), assigned user, due date, and priority.</li>
    <li>These CAPAs were moved to implementation once investigation was frozen.</li>
  </ul>

  <hr />

  <h5 className="section-title">💬 Discussion Section</h5>
  <ul>
    <li>A chat space showing the collaborations and discussions by the investigation team.</li>
    <li>Records how the team worked together to prevent recurrence.</li>
  </ul>

  <hr />

  <h5 className="section-title">📊 Right-hand Side – UAUC Analysis</h5>
  <ul>
    <li>IRA Analysis – accident statistics</li>
    <li>Near Miss Analysis</li>
    <li>UA vs UC Analysis</li>
    <li>Risk-Based Analysis</li>
    <li>Good Act & Toolbox Talk (TBT) Count</li>
    <li>Internal & External Training Count</li>
    <li>Employee & Contractor Training Count</li>
  </ul>

  <p>
    This data helps teams build a data-driven safety strategy to reduce unsafe acts and unsafe conditions.
  </p>

  <div className="alert alert-info">
    ✅ In a Completed Accident IRA, all information is frozen for record keeping. 
    No new edits can be made; only review and reference are possible.
  </div>
</div>

        </div>
      </Drawer>
    </div>
  );

  return (
    <>
      <div
        className="toggle-help-btn"
        onClick={toggle}
        role="button"
        tabIndex={0}
        onKeyPress={(e) => {
          if (e.key === "Enter" || e.key === " ") toggle();
        }}
        style={{ fontSize: "16px", cursor: "pointer" }}
      >
        Page Help
        <img src="/images/formkit_help.svg" alt="" />
      </div>
      {createPortal(drawer, document.body)}
    </>
  );
};

export default IraCompletedHelpDrawer;
